import { PromptInput } from "@/features/chat/components/PromptInput";
import { ChatSessionProvider } from "@/features/chat/context/ChatSessionProvider";
import { AGENT_CONFIG_PATH } from "@/features/chat/types";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { createFastExtraction } from "../api/curation";
import { datasetModeAtom, effectiveDatasetMode, usesFastBuilder } from "../datasetMode";
import { useDatasetModeAccess } from "../hooks/useDatasetMode";
import { DatasetModePicker } from "./DatasetModePicker";

const SAMPLE_DATASETS = [
  "Radiology images of human tumors, with imaging modality, body part, and tumor type",
  "Phase 3 GLP-1 trials for weight loss: drug, dose, participants, and weight change per arm",
  "Kaplan–Meier survival curves from phase 3 oncology trials, with cancer type and endpoint",
  "Reported IC50 values for KRAS G12C inhibitors, one row per compound and assay",
] as const;

/** Fast mode copies values as the papers write them, so these ask for such values. */
const FAST_SAMPLE_DATASETS = [
  "Case reports of drug-induced liver injury: patient age, suspected drug, and whether the patient recovered",
  "Phase 3 trials of SGLT2 inhibitors: trial name, NCT number, participants randomized, and whether it was double-blind",
  "Randomized trials of psilocybin for depression: country, number of participants, and whether a placebo was used",
  "Case reports of Brugada syndrome: gene, variant as written, and whether a relative was also affected",
] as const;

/** The dataset builder plans the columns from a request for a dataset. */
const FAST_BUILDER_SAMPLES = [
  "Build a dataset of weight loss with GLP-1 receptor agonists versus placebo, by drug and dose",
  "Build a dataset of response rates of pembrolizumab in advanced melanoma",
  "Build a dataset of trials of vitamin D supplementation for fracture prevention in older adults",
  "Build a dataset of semaglutide doses tested in phase 3 trials, with participant numbers",
  "Build a dataset of patents on KRAS G12D inhibitors",
] as const;

interface CurationComposerProps {
  /** The first message created this chat; the page moves it to the side panel. */
  onStarted: (sessionId: string) => void;
  /** The dataset builder created this Fast dataset; it goes on filling its rows. */
  onFastStarted: (datasetId: string) => void;
}

/** Starts a curation the way Paperclip Chat starts a conversation: type first. */
export const CurationComposer = ({ onStarted, onFastStarted }: CurationComposerProps) => {
  const state = useAtomValue(datasetModeAtom);
  const access = useDatasetModeAccess();
  const mode = effectiveDatasetMode(state, access);
  const fastBuilder = usesFastBuilder(state, access);
  const fast = mode === "fast";
  const comprehensive = mode === "comprehensive";
  const limited = mode === "limited";
  const [building, setBuilding] = useState(false);
  const { isLoading: flagsLoading } = useFeatureFlags();
  // Before the flags arrive the mode is not known, so a question sent then waits for them.
  const [queued, setQueued] = useState<string | null>(null);

  const buildFast = (question: string) => {
    if (building) return;
    setBuilding(true);
    createFastExtraction(question)
      .then((dataset) => onFastStarted(dataset.id))
      .catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : "The build did not start.");
      })
      .finally(() => setBuilding(false));
  };

  useEffect(() => {
    if (flagsLoading || queued === null) return;
    setQueued(null);
    if (fastBuilder) buildFast(queued);
    else toast.error("Fast mode is not available for your account. Send the question again to build it in Balanced.");
  }, [flagsLoading, queued, fastBuilder, buildFast]);

  return (
    <ChatSessionProvider
      sessionId={undefined}
      agentConfigPath={AGENT_CONFIG_PATH}
      onSessionCreated={onStarted}
      dataCurationChat
    >
      <div className="flex min-h-[calc(100dvh-7rem)] w-full flex-col justify-center py-10">
        <div className="mx-auto w-full max-w-[58rem]">
          <div className="px-6">
            <h1 className="font-display text-4xl leading-tight tracking-tight text-zinc-900">
              What dataset do you want to build?
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-600">
              {fastBuilder
                ? "Describe the dataset you want. Fast plans the columns, " +
                  "reads papers, trials and other records, and fills the table in about two minutes; " +
                  "each value is the exact text of its source."
                : "Describe the dataset you need, or what you want to use it for. " +
                  "The agent proposes the rows and columns for your review, then " +
                  "collects each value and checks it against its source."}
            </p>
            {fast && !fastBuilder && (
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">
                Fast mode works best when the values are written in the papers
                as you want them, such as names, counts, and yes or no answers.
                For values that must be calculated or summarized, use Balanced.
              </p>
            )}
            {fastBuilder && (
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">
                Fast reads papers only. You can edit the columns afterwards, and
                the chat can continue the dataset in Balanced mode.
              </p>
            )}
            {comprehensive && (
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">
                Full read mode optimizes the columns, then the search, then
                reads every paper of the cohort and checks coverage. It can take
                hours. Each turn of the chat stops after about 45 minutes while
                the maps keep running: send “continue” to go on.
              </p>
            )}
            {limited && (
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">
                Balanced (limited) shows the dataset as accounts outside GXL see it:
                with their row limit, and without the map options that only GXL
                accounts have.
              </p>
            )}
            {(building || queued !== null) && (
              <p className="mt-2 text-sm text-zinc-500" role="status">
                Planning the columns…
              </p>
            )}
          </div>
          <div className="mt-10">
            <PromptInput
              placeholder={fastBuilder ? "Ask a question about the papers..." : "Describe the dataset you want..."}
              samplePrompts={fastBuilder ? FAST_BUILDER_SAMPLES : fast ? FAST_SAMPLE_DATASETS : SAMPLE_DATASETS}
              largeSamples
              actions={<DatasetModePicker />}
              onSend={flagsLoading ? setQueued : fastBuilder ? buildFast : undefined}
            />
          </div>
        </div>
      </div>
    </ChatSessionProvider>
  );
};
